import { useAppStore } from "@/store";
import { useCallback, useState } from "react";
import { MdsModal } from "@/design-system/components/modal";
import { usePageDragAndDrop } from "@/components/drag-and-drop";
import { importDroppedItems } from "@/modules/importing/drop-and-drop-markdown";
import { mdsColors } from "@/design-system/foundations";
import { MdsIconButton } from "@/design-system/components/icon-button";
import { MdsIconKind } from "@/design-system/components/icon";
import styled from "@emotion/styled";
import { type DropItem } from "react-aria-components";
import { ImportDropTarget } from "@/components/importing";
import { EventContext } from "@/domains/metrics/context";
import { observer } from "mobx-react-lite";

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ImportModal = observer<ImportModalProps>(({ isOpen, onClose }) => {
  const { store } = useAppStore();
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = useCallback(
    (e: { items: DropItem[] }) => {
      setIsDragOver(false);
      importDroppedItems(e.items, store, EventContext.GlobalActions);
      onClose();
    },
    [store, onClose]
  );

  const handleDropEnter = useCallback(() => {
    setIsDragOver(true);
  }, []);

  const handleDropExit = useCallback(() => {
    setIsDragOver(false);
  }, []);

  usePageDragAndDrop({
    priority: 10,
    isActive: isOpen,
    onDrop: handleDrop,
    onDropEnter: handleDropEnter,
    onDropExit: handleDropExit,
  });

  return (
    <MdsModal isModalOpen={isOpen} handleCloseModal={onClose}>
      <ContentContainer>
        <HeaderRow>
          <Title>Import notes into your Mem</Title>
          <MdsIconButton iconKind={MdsIconKind.Exit} onClick={onClose} />
        </HeaderRow>
        <Description>
          Bring in any text (.txt) or Markdown (.md) files into Mem as notes.{" "}
        </Description>
        <DropTargetWrapper isActive={isDragOver}>
          <ImportDropTarget isActive={isDragOver} />
        </DropTargetWrapper>
      </ContentContainer>
    </MdsModal>
  );
});

// -------------------- Styled Components --------------------

const ContentContainer = styled.div({
  padding: 24,
  minWidth: 300,
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const HeaderRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
});

const Title = styled.h2({
  margin: 0,
});

const Description = styled.p({
  margin: 0,
});

const DropTargetWrapper = styled.div<{ isActive: boolean }>`
  border: ${({ isActive }) => (isActive ? "1px solid blue" : "1px dashed black")};
  background-color: ${({ isActive }) => (isActive ? mdsColors().blue.x700 + "05" : undefined)};
  border-radius: 4px;
  min-width: 592px;
  padding-top: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;
